import katex from 'katex';

/* ============================================================
   COMPILATION LATEX ROBUSTE
   compileLatex(raw) → { html, ok }
   - nettoie ce que le modèle envoie (double échappement, $, \[ \], code fences…)
   - mélange texte / maths ($...$) et phrases simples
   - tente une cascade de réparations avant d'abandonner
   - ne lève JAMAIS d'exception : en dernier recours, affiche le texte brut
   ============================================================ */

/* Code couleur pédagogique (une couleur = un sens)
   \ancien{}    rouge   : valeur / terme remplacé ou à éliminer
   \nouveau{}   vert    : nouvelle valeur, résultat
   \operation{} bleu    : opération appliquée, terme visé
   \attention{} orange  : point à retenir, piège
   \fond{}      surbrillance jaune d'un groupe */
const MACROS = {
  '\\R': '\\mathbb{R}', '\\N': '\\mathbb{N}', '\\Z': '\\mathbb{Z}',
  '\\Q': '\\mathbb{Q}', '\\C': '\\mathbb{C}',
  '\\ancien': '\\textcolor{##e11d48}{#1}',
  '\\nouveau': '\\textcolor{##059669}{#1}',
  '\\operation': '\\textcolor{##2563eb}{#1}',
  '\\attention': '\\textcolor{##d97706}{#1}',
  '\\fond': '\\colorbox{##fef08a}{$#1$}'
};

/* Noms de couleurs français acceptés dans \textcolor{rouge}{...} */
const COLOR_NAMES = {
  rouge: '#e11d48', vert: '#059669', bleu: '#2563eb', orange: '#d97706',
  jaune: '#facc15', violet: '#7c3aed', rose: '#db2777', gris: '#64748b'
};

const UNICODE = {
  '×': '\\times ', '÷': '\\div ', '−': '-', '–': '-', '≤': '\\leq ', '≥': '\\geq ',
  '≠': '\\neq ', '≈': '\\approx ', '²': '^{2}', '³': '^{3}', '°': '^{\\circ}',
  '→': '\\to ', 'π': '\\pi ', '√': '\\sqrt ', '∞': '\\infty ', '·': '\\cdot '
};
const UNI_RE = new RegExp('[' + Object.keys(UNICODE).join('') + ']', 'g');

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* Texte brut → contenu sûr dans \text{...} */
const escText = (t) => String(t)
  .replace(/\\/g, '\\textbackslash ')
  .replace(/([%&#_{}$])/g, '\\$1')
  .replace(/~/g, '\\textasciitilde ')
  .replace(/\^/g, '\\textasciicircum ');

/* Caractères de contrôle créés quand \t, \f, \b, \r, \n ont été interprétés
   comme des échappements (ex : « \times » devenu TAB + « imes ») */
const fixControlChars = (s) => s
  .replace(/\t(?=[a-zA-Z])/g, '\\t')
  .replace(/\f/g, '\\f')
  .replace(/\x08/g, '\\b')
  .replace(/\x0b/g, '\\v')
  .replace(/\r(?=[a-zA-Z])/g, '\\r')
  .replace(/\n(?=(?:eq|eg|ewline|abla|otin|ot|u|i|e|less|gtr|mid|parallel|sim|cong|rightarrow|leftarrow|Rightarrow)\b)/g, '\\n');

/* Nettoyage déterministe (sans jamais changer le sens) */
const normalize = (raw) => {
  let s = fixControlChars(String(raw == null ? '' : raw)).trim();

  // blocs de code ```latex ... ```
  s = s.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '');

  // double échappement : \\begin, \\frac, \\times… (aucun antislash simple devant une lettre)
  const hasSingleCmd = /(^|[^\\])\\[a-zA-Z]/.test(s);
  const hasDoubleCmd = /\\\\[a-zA-Z]/.test(s);
  if (hasDoubleCmd && !hasSingleCmd) s = s.replace(/\\\\/g, '\\');

  // couleurs en français : \textcolor{rouge}{...} → couleur de la palette
  s = s.replace(
    /\\(textcolor|color|colorbox)\{(rouge|vert|bleu|orange|jaune|violet|rose|gris)\}/g,
    (m, cmd, name) => `\\${cmd}{${COLOR_NAMES[name]}}`
  );

  // délimiteurs englobants
  s = s.replace(/^\s*\$\$([\s\S]*?)\$\$\s*$/, '$1');
  s = s.replace(/^\s*\$([^$]*)\$\s*$/, '$1');
  s = s.replace(/^\s*\\\(([\s\S]*?)\\\)\s*$/, '$1');
  s = s.replace(/^\s*\\\[([\s\S]*?)\\\]\s*$/, '$1');

  // environnements non supportés par KaTeX → équivalents
  s = s.replace(/\\begin\{(?:equation|displaymath|math)\*?\}([\s\S]*?)\\end\{(?:equation|displaymath|math)\*?\}/g, '$1');
  s = s.replace(/\\(begin|end)\{(?:align|eqnarray|flalign)\*?\}/g, '\\$1{aligned}');
  s = s.replace(/\\(begin|end)\{(?:gather|multline)\*?\}/g, '\\$1{gathered}');
  s = s.replace(/\\(begin|end)\{(?:tabular|tabularx)\}/g, '\\$1{array}');

  // sauts de ligne finaux inutiles
  s = s.replace(/(\\\\\s*)+$/, '').trim();
  return s;
};

const processMath = (s) => s
  .replace(UNI_RE, (c) => UNICODE[c])
  .replace(/(^|[^\\])%/g, '$1\\%') // % seul = commentaire en LaTeX : on l'affiche tel quel
  .replace(/(\d),(?=\d)/g, '$1{,}'); // virgule décimale française : 12,50 → 12{,}50

const looksLikeProse = (s) =>
  /[A-Za-zÀ-ÿ]{3,}\s+[A-Za-zÀ-ÿ]{2,}/.test(s) && !/[\\^_=]/.test(s);

/* Construit l'expression : texte seul, maths seules, ou mélange $...$ */
const buildExpression = (s) => {
  if (!s) return '';
  if (s.includes('$')) {
    return s.split('$').map((part, i) => {
      if (i % 2 === 1) return processMath(part);
      return part.trim() ? `\\text{${escText(part)}}` : '';
    }).join(' ');
  }
  if (looksLikeProse(s)) return `\\text{${escText(s)}}`;
  return processMath(s);
};

/* ---------------- RÉPARATIONS ---------------- */
const balanceBraces = (s) => {
  let depth = 0;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '\\') { out += ch + (s[i + 1] || ''); i++; continue; }
    if (ch === '{') depth++;
    if (ch === '}') { if (depth === 0) continue; depth--; }
    out += ch;
  }
  return out + '}'.repeat(depth);
};

const closeEnvs = (s) => {
  const stack = [];
  const re = /\\(begin|end)\{([^}]*)\}/g;
  let m;
  while ((m = re.exec(s))) {
    if (m[1] === 'begin') stack.push(m[2]);
    else {
      const i = stack.lastIndexOf(m[2]);
      if (i !== -1) stack.splice(i);
    }
  }
  return s + stack.reverse().map((n) => `\\end{${n}}`).join('');
};

const stripUnsupported = (s) => s
  .replace(/\\(?:usepackage|documentclass)(?:\[[^\]]*\])?\{[^}]*\}/g, '')
  .replace(/\\(?:label|ref|eqref|vspace\*?|hspace\*?|cline)\{[^}]*\}/g, '')
  .replace(/\\(?:num|unit)\{([^}]*)\}/g, '$1')
  .replace(/\\newline(?![a-zA-Z])/g, '\\\\')
  .replace(/\\(?:nonumber|notag|noindent|centering|hfill|par|bigskip|medskip|smallskip|allowbreak)(?![a-zA-Z])/g, '');

const noLeftRight = (s) => s
  .replace(/\\(?:left|right|middle)\s*\./g, '')
  .replace(/\\(?:left|right|middle)(?![a-zA-Z])\s*/g, '');

/* Remplace la spécification de colonnes de tout \begin{array} par une spécification sûre */
const forceArraySpec = (s) => {
  const tag = '\\begin{array}';
  let out = '';
  let i = 0;
  for (;;) {
    const j = s.indexOf(tag, i);
    if (j === -1) { out += s.slice(i); break; }
    out += s.slice(i, j) + tag;
    let k = j + tag.length;
    while (k < s.length && /\s/.test(s[k])) k++;
    if (s[k] === '{') {
      let d = 0;
      let e = k;
      for (; e < s.length; e++) {
        if (s[e] === '\\') { e++; continue; }
        if (s[e] === '{') d++;
        else if (s[e] === '}') { d--; if (d === 0) break; }
      }
      i = e + 1;
    } else {
      i = k;
    }
    out += '{rrrrrrrr}';
  }
  return out;
};

/* Cascade de variantes, de la plus fidèle à la plus tolérante */
const attempts = (expr) => {
  const list = [expr];
  let c = closeEnvs(balanceBraces(expr)); list.push(c);
  c = stripUnsupported(c); list.push(c);
  c = forceArraySpec(c); list.push(c);
  c = noLeftRight(c); list.push(c);
  if (!/\\begin\{/.test(c) && /&|\\\\/.test(c)) list.push(`\\begin{aligned} ${c} \\end{aligned}`);
  return list;
};

/* ---------------- RENDU ---------------- */
const renderStr = (s) => katex.renderToString(s, {
  throwOnError: true,
  displayMode: false,
  strict: 'ignore',
  trust: false,
  maxExpand: 1000,
  macros: { ...MACROS }
});

/* Rend une variante ; si une commande est inconnue, on la retire et on réessaie */
const tryRender = (expr) => {
  let cur = expr;
  for (let n = 0; n < 8; n++) {
    try {
      return renderStr(cur);
    } catch (e) {
      const m = /Undefined control sequence:\s*(\\[a-zA-Z]+|\\.)/.exec((e && e.message) || '');
      if (!m) throw e;
      cur = cur.replace(new RegExp(escapeRegExp(m[1]) + '(?![a-zA-Z])', 'g'), ' ');
    }
  }
  throw new Error('unrenderable');
};

export const compileLatex = (raw) => {
  try {
    const expr = buildExpression(normalize(raw));
    if (!expr.trim()) return { html: '', ok: true };
    for (const candidate of attempts(expr)) {
      try { return { html: tryRender(candidate), ok: true }; } catch (e) { /* variante suivante */ }
    }
  } catch (e) { /* on passe au texte brut */ }

  // Dernier recours : le texte brut, toujours affichable
  const plain = String(raw == null ? '' : raw).replace(/\$/g, '').trim();
  try { return { html: renderStr(`\\text{${escText(plain)}}`), ok: false }; }
  catch (e) { return { html: `<span>${escapeHtml(plain)}</span>`, ok: false }; }
};

export default compileLatex;